
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import MatureContentService from '@/services/mature-content-service';

export const useMatureContent = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Initialize synchronously from localStorage to prevent race conditions
  const initialSettings = MatureContentService.getLocalSettings();
  const [matureContentEnabled, setMatureContentEnabled] = useState(
    initialSettings.ageVerified && initialSettings.matureContentEnabled
  );
  const [ageVerified, setAgeVerified] = useState(initialSettings.ageVerified);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const updateMatureContentState = () => {
      const settings = MatureContentService.getLocalSettings();
      setAgeVerified(settings.ageVerified);
      setMatureContentEnabled(settings.ageVerified && settings.matureContentEnabled);
    };

    // Update from local storage changes

    // Load settings from database on user change (async, non-blocking)
    if (user?.id) {
      setIsLoading(true);
      MatureContentService.loadAndSyncSettings(user.id)
        .then(() => {
          updateMatureContentState();
        })
        .catch(error => {
          console.error('Failed to load mature content settings:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    // Listen for localStorage changes (from other tabs or components)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'age_verified' || e.key === 'allow_mature_content') {
        updateMatureContentState();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [user?.id, queryClient]);

  return {
    matureContentEnabled,
    ageVerified,
    showMatureContent: matureContentEnabled,
    isLoading
  };
};
